import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

export type ButtonVariant =
  | 'primary'
  | 'success'
  | 'outline'
  | 'outline-danger'
  | 'ghost'
  | 'danger'
  | 'default'
  | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render the single child element with button styling instead of a <button>. */
  asChild?: boolean;
}

/**
 * ⚠️ `baseClasses` below carries a bare `border`, so in Tailwind v4 an unqualified variant would
 * paint a near-black `currentColor` line. The 2026-10-03 P0 fix batch (§9.3b) required an audit of
 * every entry here for exactly that.
 *
 * AUDIT RESULT: clean, no change needed. All eight variants already set an explicit border colour,
 * including `ghost` and `link`, which set `border-transparent` — the two the design flagged as the
 * likely offenders. Keep it that way: ANY new variant added here MUST name a border colour or
 * `border-transparent`, or it inherits the bug globally.
 */
const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-primary border-primary text-white hover:opacity-90',
  success: 'bg-success border-success text-white hover:opacity-90',
  outline: 'bg-white border-outlines text-text-primary hover:bg-subtle-bg',
  'outline-danger': 'bg-white border-error/40 text-error hover:bg-error/5',
  ghost: 'bg-transparent border-transparent text-text-secondary hover:bg-subtle-bg',
  danger: 'bg-error border-error text-white hover:opacity-90',
  // Aliases mapped to productx tokens for backward compatibility with shadcn call sites.
  default: 'bg-primary border-primary text-white hover:opacity-90',
  link: 'bg-transparent border-transparent text-primary underline-offset-4 hover:underline',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-[42px] px-4 text-sm font-semibold',
  lg: 'h-11 px-5 text-base font-semibold',
  // Square icon-only button — matches the md control height.
  icon: 'h-[42px] w-[42px] p-0',
};

const baseClasses =
  'inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-[var(--radius-button)] border font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-50';

/**
 * Compose the button class string outside of the React component.
 * Used by call sites that style non-button elements as buttons (e.g. alert-dialog).
 */
export function buttonVariants(opts?: { variant?: ButtonVariant; size?: ButtonSize }): string {
  const variant = opts?.variant ?? 'primary';
  const size = opts?.size ?? 'md';
  return cn(baseClasses, variantClasses[variant], sizeClasses[size]);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(baseClasses, variantClasses[variant], sizeClasses[size], className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
